import { useMutation, useQueryClient } from "@tanstack/react-query";
import { HTTPError } from "ky";
import { api } from "@/api/client";
import type { BookingResponse, ReminderChannel } from "@/api/types";
import { isErrorEnvelope } from "@/lib/handle-form-error";
import { assertCurrentSession, sessionRevision } from "@/lib/session-request-scope";

export function useUpdateBookingReminders() {
  const queryClient = useQueryClient();

  return useMutation({
    onMutate: ({
      id,
    }: {
      id: string;
      reminder_channels: ReminderChannel[];
      expected_reminder_channel_versions?: Partial<Record<ReminderChannel, string>>;
    }) => queryClient.cancelQueries({ queryKey: ["booking", id], exact: true }),
    mutationFn: ({ id, reminder_channels, expected_reminder_channel_versions }) =>
      api
        .patch(`bookings/${encodeURIComponent(id)}`, {
          json: { reminder_channels, expected_reminder_channel_versions },
        })
        .json<BookingResponse>(),
    onSuccess: async (booking, { id }) => {
      const revision = sessionRevision();
      await queryClient.cancelQueries({ queryKey: ["booking", id], exact: true });
      assertCurrentSession(revision);
      queryClient.setQueryData(["booking", id], booking);
      void queryClient.invalidateQueries({ queryKey: ["bookings"] });
      void queryClient.invalidateQueries({ queryKey: ["booking", id], exact: true });
    },
    onError: (error, { id }) => {
      if (
        error instanceof HTTPError &&
        isErrorEnvelope(error.data) &&
        error.data.errors.some((entry) => entry.code === "reminder_contact_changed")
      ) {
        void queryClient.invalidateQueries({ queryKey: ["me"], exact: true });
        void queryClient.invalidateQueries({ queryKey: ["booking", id], exact: true });
      }
    },
  });
}
